import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { ArrowLeft, Search, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { toLocalDateString } from '../../utils/dateUtils'
import QuickOrderItemRow from './QuickOrderItemRow'

const toInputDateString = (date) => {
  const d = date instanceof Date ? date : new Date(date)
  return toLocalDateString(d)
}

export default function QuickOrderForm({
  order,
  customers,
  products,
  productTypes,
  loadPriceLists,
  loadBranches,
  multiplierToDiscountPercent,
  onSave,
  onDelete,
  onCancel
}) {
  const isEdit = !!order
  const isInvoiced = order?.status === 'invoiced'
  const readOnly = isInvoiced

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState(order?.customer_id || '')
  const [selectedCustomerName, setSelectedCustomerName] = useState(order?.customer?.name || '')
  const [selectedBranch, setSelectedBranch] = useState(order?.branch_id || '')
  const [customerBranches, setCustomerBranches] = useState([])
  const [selectedDate, setSelectedDate] = useState(() => {
    if (order?.date) return new Date(order.date + 'T00:00:00')
    return new Date()
  })
  const [saleItems, setSaleItems] = useState([])
  const [priceLists, setPriceLists] = useState([])
  const [status, setStatus] = useState(order?.status || 'pending')
  const [saving, setSaving] = useState(false)

  // Customer picker sheet
  const [pickerOpen, setPickerOpen] = useState(false)
  const [customerSearch, setCustomerSearch] = useState('')

  // Initialise sale items from products (or from existing order)
  useEffect(() => {
    if (!products.length || !productTypes.length) return

    const defaultProductType = productTypes.find(pt => pt.name === 'A Class') || productTypes[0]

    if (isEdit && order?.sale_items) {
      // Pre-populate from existing order — set all products, fill quantities from existing items
      const existingMap = new Map(
        order.sale_items.map(si => [si.product_id, si])
      )

      const items = products.map(product => {
        const existing = existingMap.get(product.id)
        if (existing) {
          return {
            product_id: product.id,
            productName: product.strain,
            product_type_id: existing.product_type_id || defaultProductType?.id,
            quantity: existing.quantity,
            price: existing.price,
            subtotal: existing.quantity * existing.price
          }
        }
        return {
          product_id: product.id,
          productName: product.strain,
          product_type_id: defaultProductType?.id || null,
          quantity: 0,
          price: product.base_price_per_kg || 0,
          subtotal: 0
        }
      })
      setSaleItems(items)
    } else {
      const items = products.map(product => ({
        product_id: product.id,
        productName: product.strain,
        product_type_id: defaultProductType?.id || null,
        quantity: 0,
        price: product.base_price_per_kg || 0,
        subtotal: 0
      }))
      setSaleItems(items)
    }
  }, [products, productTypes, order, isEdit])

  // Load branches when customer is set (for edit mode)
  useEffect(() => {
    if (selectedCustomer && isEdit) {
      loadBranches(selectedCustomer).then(branches => {
        setCustomerBranches(branches || [])
      })
      // Load price lists for edit mode too
      const customer = customers.find(c => c.id === selectedCustomer)
      if (customer) {
        const customerType = customer.type || customer.branches?.[0]?.customer_type?.name
        if (customerType) {
          loadPriceLists(customerType).then(pl => setPriceLists(pl || []))
        }
      }
    }
  }, [selectedCustomer, isEdit, loadBranches, loadPriceLists, customers])

  const handleCustomerSelect = useCallback(async (customer) => {
    setSelectedCustomer(customer.id)
    setSelectedCustomerName(customer.name)
    setPickerOpen(false)

    // Load branches
    const branches = await loadBranches(customer.id)
    setCustomerBranches(branches || [])

    if (branches?.length === 1) {
      setSelectedBranch(branches[0].id)
    } else {
      setSelectedBranch('')
    }

    // Load price lists and update pricing
    const customerType = customer.type || customer.branches?.[0]?.customer_type?.name
    if (customerType) {
      const pl = await loadPriceLists(customerType)
      setPriceLists(pl || [])

      // Update item prices
      setSaleItems(prev => prev.map(item => {
        const product = products.find(p => p.id === item.product_id)
        const priceList = (pl || []).find(p =>
          p.product_id === item.product_id &&
          p.customer_type === customerType.toLowerCase()
        )
        const basePrice = priceList?.price || product?.base_price_per_kg || 0
        const ptData = productTypes.find(pt => pt.id === item.product_type_id)
        const multiplier = ptData?.price_multiplier ?? 1.0
        const finalPrice = basePrice * multiplier
        return {
          ...item,
          price: finalPrice,
          subtotal: item.quantity * finalPrice
        }
      }))
    }
  }, [loadBranches, loadPriceLists, products, productTypes])

  const handleQuantityChange = useCallback((productId, value) => {
    const numQuantity = parseFloat(value) || 0
    setSaleItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        return {
          ...item,
          quantity: numQuantity,
          subtotal: numQuantity * item.price
        }
      }
      return item
    }))
  }, [])

  const handleProductTypeChange = useCallback((productId, productTypeId) => {
    setSaleItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        const customer = customers.find(c => c.id === selectedCustomer)
        const customerType = (customer?.type || '').toLowerCase()
        const priceList = priceLists.find(pl =>
          pl.product_id === productId &&
          pl.customer_type === customerType
        )
        const product = products.find(p => p.id === productId)
        const basePrice = priceList?.price || product?.base_price_per_kg || item.price || 0
        const selectedType = productTypes.find(pt => pt.id === productTypeId)
        const multiplier = selectedType?.price_multiplier ?? 1.0
        const finalPrice = basePrice * multiplier
        return {
          ...item,
          product_type_id: productTypeId,
          price: finalPrice,
          subtotal: item.quantity * finalPrice
        }
      }
      return item
    }))
  }, [customers, selectedCustomer, priceLists, products, productTypes])

  const getTotalAmount = () => {
    return saleItems.reduce((sum, item) => sum + item.subtotal, 0)
  }

  const handleSave = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer')
      return
    }
    if (customerBranches.length > 0 && !selectedBranch) {
      toast.error('Please select a branch')
      return
    }
    const itemsToSave = saleItems.filter(item => item.quantity > 0)
    if (itemsToSave.length === 0) {
      toast.error('Please add at least one item')
      return
    }

    setSaving(true)
    try {
      const saleData = {
        customer_id: selectedCustomer,
        branch_id: selectedBranch || null,
        date: toInputDateString(selectedDate),
        ...(isEdit && { status })
      }

      const items = itemsToSave.map(item => {
        const productType = productTypes.find(pt => pt.id === item.product_type_id)
        const priceMultiplier = productType?.price_multiplier ?? 1.0
        const discountPercent = multiplierToDiscountPercent(priceMultiplier)
        return {
          product_id: item.product_id,
          product_type_id: item.product_type_id,
          quantity: item.quantity,
          price: item.price,
          discount_percent: discountPercent,
          discount_note: discountPercent > 0 ? productType.name : null
        }
      })

      await onSave(saleData, items)
      toast.success(isEdit ? 'Order updated' : 'Order created')
    } catch (err) {
      console.error('Error saving order:', err)
      toast.error('Failed to save order. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setSaving(true)
    try {
      await onDelete(order.id)
      toast.success('Order deleted')
    } catch (err) {
      console.error('Error deleting order:', err)
      toast.error('Failed to delete order')
    } finally {
      setSaving(false)
    }
  }

  const filteredCustomers = customerSearch
    ? customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
    : customers

  return (
    <div className="flex flex-col min-h-0">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 -mx-6 -mt-6 px-4 py-3 bg-white border-b sticky top-14 md:top-0 z-30">
        <Button variant="ghost" size="sm" onClick={onCancel} className="p-2">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-xl font-bold">
          {isInvoiced ? 'View Order' : isEdit ? 'Edit Order' : 'New Order'}
        </h2>
      </div>

      {isInvoiced && (
        <div className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700">This order has been invoiced and cannot be edited.</p>
        </div>
      )}

      {/* Form fields */}
      <div className="space-y-4 mb-6">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Customer</Label>
          {readOnly ? (
            <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
              {selectedCustomerName || 'Unknown'}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={saving}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className={selectedCustomer ? 'text-foreground' : 'text-muted-foreground'}>
                {selectedCustomerName || 'Select Customer'}
              </span>
              <Search className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Branch</Label>
          {readOnly ? (
            <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
              {customerBranches.find(b => b.id === selectedBranch)?.name || order?.branch?.name || 'N/A'}
            </div>
          ) : (
            <Select
              value={selectedBranch}
              onValueChange={setSelectedBranch}
              disabled={!selectedCustomer || customerBranches.length === 0 || saving}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    !selectedCustomer
                      ? 'Select customer first'
                      : customerBranches.length === 0
                        ? 'No branches'
                        : 'Select Branch'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {customerBranches.map(branch => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Date</Label>
          {readOnly ? (
            <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
              {selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          ) : (
            <input
              type="date"
              value={toInputDateString(selectedDate)}
              onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00:00'))}
              disabled={saving}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 appearance-none"
            />
          )}
        </div>

        {isEdit && !isInvoiced && (
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Status</Label>
            <Select value={status} onValueChange={setStatus} disabled={saving}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Products */}
      <div className="border rounded-lg bg-white overflow-hidden">
        <div className="px-3 py-2 bg-muted/50 border-b">
          <Label className="text-sm font-medium text-muted-foreground">Products</Label>
        </div>
        <div className="divide-y">
          {saleItems.map(item => (
            <QuickOrderItemRow
              key={item.product_id}
              item={item}
              productTypes={productTypes}
              readOnly={readOnly}
              onQuantityChange={handleQuantityChange}
              onProductTypeChange={handleProductTypeChange}
            />
          ))}
        </div>
      </div>

      {/* Spacer for fixed footer */}
      <div className="h-48" />

      {/* Fixed footer */}
      {!readOnly && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 space-y-3 z-50"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="max-w-lg mx-auto space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="text-2xl font-bold text-green-700">£{getTotalAmount().toFixed(2)}</span>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onCancel} disabled={saving} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-green-700 hover:bg-green-800">
                {saving ? 'Saving...' : isEdit ? 'Update Order' : 'Save Order'}
              </Button>
            </div>
            {isEdit && (order?.status === 'pending' || order?.status === 'confirmed') && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full text-destructive hover:text-destructive" disabled={saving}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Order
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Order</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this order for {selectedCustomerName}. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      )}

      {/* Customer Picker Sheet */}
      <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
        <SheetContent side="bottom" className="h-[70vh] flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-3">
            <SheetTitle>Select Customer</SheetTitle>
          </SheetHeader>
          <div className="px-6 pb-3">
            <Input
              placeholder="Search customers..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredCustomers.map(customer => (
              <button
                key={customer.id}
                onClick={() => handleCustomerSelect(customer)}
                className={cn(
                  'w-full text-left px-6 py-3 hover:bg-accent/50 transition-colors',
                  'border-b text-sm',
                  customer.id === selectedCustomer && 'bg-accent font-medium'
                )}
              >
                <span>{customer.name}</span>
                {customer.type && (
                  <span className="text-xs text-muted-foreground ml-2">({customer.type})</span>
                )}
              </button>
            ))}
            {filteredCustomers.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No customers found</p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
